import { Appointment, AvailabilitySlot } from '@dialogue/timekeeper'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/native'

import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { useProviderBasedAvailabilities } from 'APP/Hooks/useGetAvailabilities'
import I18n from 'APP/I18n'
import AppointmentBookingRedux, { DATE_FORMAT } from 'APP/Redux/AppointmentBookingRedux'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import {
  selectCreateAppointmentRequest,
  selectSelectedDay,
  selectSelectedPreferencesWithLabels,
  selectSelectedProviderId,
} from 'APP/Store/ProviderBooking/selectors'

import type { BookingFlowProps } from 'APP/Components/AppointmentBooking/AppointmentBookingBase/types'
import { CreateAppointmentPayload } from 'APP/Store/AppointmentBooking/types'
import { ProviderSelection } from 'APP/Components/AppointmentBooking/ProviderBased/providerSelection'
import ExpiredSchedule from 'APP/Components/AppointmentBooking/common/expiredSchedule'
import { Checkout, CHECKOUT_TYPE } from 'APP/Components/Checkout'
import BottomDrawer from 'APP/Converse/BottomDrawer'
import { FEE_TYPES } from 'APP/Lib/AppointmentManagementHelpers'
import { Camelized, camelizeKeys, decamelizeKeys } from 'humps'

const Container = styled.View`
  flex: 1;
`

const StyledScrollView = styled.ScrollView.attrs({
  contentContainerStyle: {
    flexGrow: 1,
  },
})`
  flex-grow: 1;
`

export const ProviderAppointmentBooking = (props: BookingFlowProps) => {
  const dispatch = useDispatch()
  const isAppointmentCreatedRef = useRef(false)
  const {
    close,
    onComplete,
    onError,
    appointment_type,
    booking_id,
    date_from,
    date_until,
    booking,
    isLoadingBooking,
    isBookingReady,
    customHeaderText,
  } = props

  const {
    data: createdAppointment,
    error: appointmentCreationError,
    loading: isCreatingAppointment,
  } = useSelector(selectCreateAppointmentRequest)
  const selectedPreferencesWithLabels = useSelector(selectSelectedPreferencesWithLabels)
  const selectedDay = useSelector(selectSelectedDay)
  const selectedProviderId = useSelector(selectSelectedProviderId)
  const previousBookingRef = useRef(booking)

  const isExpired = moment(date_until).isBefore(moment())

  const availabilitiesParams = useMemo(
    () => ({ appointment_type, booking_id, date_from, date_until }),
    [appointment_type, booking_id, date_from, date_until]
  )

  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)
  const [showPaymentScreen, setShowPaymentScreen] = useState(false)
  const [availabilitySlot, setAvailabilitySlot] = useState<AvailabilitySlot | null>(null)

  const onAppointmentCreationError = useCallback(() => {
    setIsErrorModalVisible(true)
    setShowPaymentScreen(false)
    onError?.(appointmentCreationError)
    dispatch(providerBookingActions.resetAppointment())
  }, [dispatch, onError, appointmentCreationError])

  const { handleGetAvailabilities } = useProviderBasedAvailabilities(availabilitiesParams)

  const getSelectedDayAvailabilities = useCallback(() => {
    if (selectedDay) {
      handleGetAvailabilities(moment(selectedDay, DATE_FORMAT))
    }
  }, [selectedDay, handleGetAvailabilities])

  const handleQuit = useCallback(() => {
    onComplete({
      display: I18n.t('AppointmentBooking.get_help_scheduling_later'),
      payload: { is_booked: false },
    })
    close?.()
  }, [onComplete, close])

  const memberPreference = useMemo(
    () =>
      selectedPreferencesWithLabels
        ? {
            genders: selectedPreferencesWithLabels.genders.map((attribute) => attribute.en),
            cultural_backgrounds: selectedPreferencesWithLabels.cultural_backgrounds.map(
              (attribute) => attribute.en
            ),
            additional_interests: selectedPreferencesWithLabels.additional_interests.map(
              (attribute) => attribute.en
            ),
          }
        : null,
    [selectedPreferencesWithLabels]
  )

  const handleSuccessfulAppointmentCreated = useCallback(
    (bookedAppt: Appointment) => {
      const displayMessage = moment.tz(bookedAppt.start_at, moment.tz.guess()).format('LLL')
      const payload = {
        is_booked: true,
        ...bookedAppt,
        // the following two properties are required by BM
        // They're Emerald/Input Health artifacts, and cannot be safely removed yet.
        until_at: bookedAppt.end_at,
        service_id: -1,
        member_preference: memberPreference,
      }

      onComplete?.({ display: displayMessage, payload }, bookedAppt)
      close?.()
    },
    [onComplete, close, memberPreference]
  )

  const handleCreateAppointment = useCallback(
    (slot: AvailabilitySlot | null) => {
      // Ensure timeslot is given
      if (!slot) return false

      const payload: CreateAppointmentPayload = {
        bookingId: booking_id,
        start_at: slot.start_at,
        end_at: slot.end_at,
        provider_id: slot.provider_id,
      }

      dispatch(providerBookingActions.createAppointment(payload))
    },
    [booking_id, dispatch]
  )

  const handleConfirmAppointment = useCallback(
    (slot: AvailabilitySlot | null) => {
      if (slot == null) return false
      if (!isBookingReady) return false

      if (booking?.dialogue_metadata[FEE_TYPES.ENCOUNTER_FEE] > 0) {
        setAvailabilitySlot(slot)
        setShowPaymentScreen(true)

        return true
      } else {
        handleCreateAppointment(slot)

        return false
      }
    },
    [isBookingReady, booking, handleCreateAppointment]
  )

  const handleErrorModalClose = useCallback(() => {
    setIsErrorModalVisible(false)
    getSelectedDayAvailabilities()
    // Refetch the provider's availabilities to ensure the list is up-to-date.
    if (selectedProviderId) {
      dispatch(
        providerBookingActions.getProviderAvailabilities({
          bookingId: booking_id,
          providerId: selectedProviderId,
          start_at: date_from,
          end_at: date_until,
        })
      )
    }
  }, [
    getSelectedDayAvailabilities,
    selectedProviderId,
    booking_id,
    date_from,
    date_until,
    dispatch,
  ])

  useEffect(() => {
    dispatch(providerBookingActions.selectDay(moment(date_from).format(DATE_FORMAT)))
  }, [date_from, dispatch])

  // Each day's slots are searched when the day is selected.
  useEffect(() => {
    getSelectedDayAvailabilities()
  }, [getSelectedDayAvailabilities])

  // Refetch availabilities whenever the booking changes after its initial load (e.g. a
  // preference update), so the search runs against the freshly persisted constraints. The
  // initial load is already covered by the fetches above, so skip that first value.
  // The search reads the preferences server-side from the booking; the dots are refreshed
  // along with it.
  useEffect(() => {
    const previousBooking = previousBookingRef.current
    previousBookingRef.current = booking
    if (!booking || !previousBooking || booking === previousBooking) return

    dispatch(AppointmentBookingRedux.getAvailabilitiesExists({ bookingId: booking_id }))
    getSelectedDayAvailabilities()
  }, [booking, booking_id, getSelectedDayAvailabilities, dispatch])

  useEffect(() => {
    if (createdAppointment && !isCreatingAppointment && !isAppointmentCreatedRef.current) {
      isAppointmentCreatedRef.current = true
      handleSuccessfulAppointmentCreated(createdAppointment)
    }
  }, [createdAppointment, isCreatingAppointment, handleSuccessfulAppointmentCreated])

  useEffect(() => {
    if (appointmentCreationError && !isCreatingAppointment) {
      onAppointmentCreationError()
    }
  }, [appointmentCreationError, isCreatingAppointment, onAppointmentCreationError])

  return (
    <Container testID="PROVIDER_APPOINTMENT_BOOKING">
      <StyledScrollView>
        {showPaymentScreen && availabilitySlot && booking && (
          <Checkout
            type={CHECKOUT_TYPE.APPOINTMENT}
            context={{
              availabilitySlot: camelizeKeys(availabilitySlot) as Camelized<AvailabilitySlot>,
            }}
            // TODO: Remove indexed support once timekeeper migration is complete
            // Reference: https://github.com/dialoguemd/member-app/pull/7445#discussion_r3768109545
            feePolicy={{
              encounterFeeAmount:
                booking.dialogue_metadata[FEE_TYPES.ENCOUNTER_FEE][0] ||
                booking.dialogue_metadata[FEE_TYPES.ENCOUNTER_FEE],
              memberLateCancellationAmount:
                booking.dialogue_metadata[FEE_TYPES.LATE_CANCELLATION][0] ||
                booking.dialogue_metadata[FEE_TYPES.LATE_CANCELLATION],
              memberNoShowAmount:
                booking.dialogue_metadata[FEE_TYPES.NO_SHOW][0] ||
                booking.dialogue_metadata[FEE_TYPES.NO_SHOW],
            }}
            onConfirm={async (ctx) => {
              handleCreateAppointment(decamelizeKeys(ctx.availabilitySlot) as AvailabilitySlot)
            }}
          />
        )}

        {!showPaymentScreen &&
          (isExpired ? (
            <ExpiredSchedule quitScheduling={handleQuit} />
          ) : (
            <ProviderSelection
              bookingId={booking_id}
              dateFrom={date_from}
              dateUntil={date_until}
              onConfirm={handleConfirmAppointment}
              onQuit={handleQuit}
              appointmentType={appointment_type}
              confirmDisabled={!isBookingReady || isLoadingBooking}
              customHeaderText={customHeaderText}
            />
          ))}
      </StyledScrollView>

      <BottomDrawer
        isVisible={isErrorModalVisible}
        onClose={handleErrorModalClose}
        closeButtonProps={{ hidden: true }}
        title={I18n.t('AppointmentBooking.book_appointment_error.title')}
        subtitle={I18n.t('AppointmentBooking.book_appointment_error.subtitle')}
        primaryButtonProps={{
          title: I18n.t('AppointmentBooking.book_appointment_error.cta'),
          onPress: handleErrorModalClose,
          widthVariant: 'full',
          small: false,
          analyticsName: 'appointmentBooking.appointmentCreationError.seeNextAvailableTimes',
        }}
      />
    </Container>
  )
}
